import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { SUBSCRIPTION_PLAN as SUBSCRIPTION_PLAN_LINK } from 'constants/paths';
import { useBreakpoints } from 'hooks';
import { useBusinessToolSessionStore, useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getAIAssistantReadinessPercent,
  getBusinessPlan,
  getBusinessPlanType,
  getBusinessToolMode,
} from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { ReactComponent as ExclamationCircleIcon } from 'assets/icons/ExclamationCircleIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useExamplePreview } from 'hooks/useExamplePreview';
import { useIsBusinessPlanOwner } from 'hooks/useIsBusinessPlanOwner';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Responsive } from 'components/_basic/Responsive';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import ModalContext from 'components/ModalManager/modal-context';
import { OnePageTypeSwitch } from 'components/OnePage';
import { SaveProjectModal } from 'components/SaveProjectModal/SaveProjectModal';
import { ShareDocumentWrapper } from 'components/ShareDocumentWrapper';

import { GenerateWithAiButton } from './components/GenerateWithAIButton';
import { PdfDownloadButton } from './PdfDownloadButton';
import { BusinessToolTitleInput } from '../BusinessToolTitleInput';
import { BusinessToolType } from '../BusinessToolType';
import { useBusinessToolAiContext } from '../components/BusinessToolAiProvider';
import { checkIsOnePageBP, checkIsPreviewMode } from '../utils';

import styles from './BusinessToolHeader.module.scss';

import scssVariables from 'styles/sass-variables.module.scss';

interface IBusinessToolHeaderProps {
  className?: string;
}

export const BusinessToolHeader: React.FC<IBusinessToolHeaderProps> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();
  const { isAuthenticated } = useAuthenticated();
  const { showModal, hideModal } = useContext(ModalContext);
  const { isLoading, generateWholeDocument } = useBusinessToolAiContext();
  const businessToolMode = useBusinessToolSessionStore(getBusinessToolMode);
  const isBusinessPlanOwner = useIsBusinessPlanOwner();
  const { isExamplePage } = useExamplePreview();
  const isEditAvailable = isBusinessPlanOwner || (!isAuthenticated && !isExamplePage);

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const isGenerateWholeDocumentDisabled = businessPlan.metadata.isGeneratedWholeDocument && !isStandardOrLightTariff;

  const isShareButtonAvailable = isBusinessPlanOwner || (!isAuthenticated && !isExamplePage);

  const AIAssistantReadinessPercent = useBusinessToolStore(getAIAssistantReadinessPercent);
  const bpType = useBusinessToolStore(getBusinessPlanType);
  const isOnePageBP = checkIsOnePageBP(bpType);

  const aiDisabled = AIAssistantReadinessPercent < 100;

  const isPreviewMode = checkIsPreviewMode(businessToolMode);

  const isPDFDownloadAvailable = isStandardOrLightTariff;

  const pdfButtonTooltip = () => {
    return (
      <Box className={styles.tooltipContainer}>
        <Box className={styles.tooltipIcon}>
          <ExclamationCircleIcon fill={scssVariables.orange_600} width={32} height={32} />
        </Box>
        <Typography variant="subtitle2" onClick={() => navigate(SUBSCRIPTION_PLAN_LINK)} className={styles.tooltipText}>
          <Link to="/subscription-plans" className={styles.linkStyle}>
            {t('upgradePlanToGetFeature1')}
          </Link>{' '}
          {t('upgradePlanToGetFeature2')}
        </Typography>
      </Box>
    );
  };

  localStorage.removeItem('address');
  localStorage.removeItem('companyName');
  localStorage.removeItem('projectBriefDescription');
  localStorage.removeItem('companyBriefDescription');

  const handleAIButtonClick = () =>
    isAuthenticated
      ? isGenerateWholeDocumentDisabled
        ? () => null
        : generateWholeDocument()
      : showModal(SaveProjectModal, {
          onClose: hideModal,
          title: t('pleaseLogin'),
          description: t('loginForAIGeneration'),
        });

  return (
    <Box className={clsx(styles.container, className)}>
      <Box className={styles.headerGroup}>
        <Stack spacing={{ xs: 0.5, sm: 3 }} alignItems="center" direction="row" height={28}>
          <Typography className={styles['title']} variant="h4">
            {t('forBankHeader')}
          </Typography>
          {isEditAvailable && <BusinessToolType />}
        </Stack>
        <BusinessToolTitleInput readOnly={isExamplePage} />
      </Box>

      <Box className={styles.buttonGroup}>
        {!isPreviewMode && (
          <TooltipComponent
            title={pdfButtonTooltip()}
            isTooltipHidden={!isGenerateWholeDocumentDisabled}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
          >
            <GenerateWithAiButton
              aiDisabled={aiDisabled}
              isLoading={isLoading}
              onClick={handleAIButtonClick}
              isGenerateWholeDocumentDisabled={!!isGenerateWholeDocumentDisabled}
            />
          </TooltipComponent>
        )}

        {!isOnePageBP && (
          <TooltipComponent
            title={pdfButtonTooltip()}
            isTooltipHidden={isPDFDownloadAvailable}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
            isTitleIsNode
          >
            <PdfDownloadButton />
          </TooltipComponent>
        )}
        {isShareButtonAvailable && (
          <TooltipComponent
            title={pdfButtonTooltip()}
            isTooltipHidden={isPDFDownloadAvailable}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
            isTitleIsNode
          >
            <ShareDocumentWrapper />
          </TooltipComponent>
        )}
        <Responsive mobile>{isOnePageBP && <OnePageTypeSwitch />}</Responsive>
      </Box>
    </Box>
  );
};
